// Keeps keyboard focus inside an open modal (TTTL-186). The backdrop is
// portalled to the body, so without this a Tab past the card's last
// control walks out to the page underneath the backdrop.

/** Where a Tab press should land, or null when the browser's own move is
 *  already inside the trap: forward from the last stop wraps to the first,
 *  backward from the first wraps to the last, and from anywhere that is
 *  not a stop (the card itself) Tab enters at the first stop and Shift+Tab
 *  at the last. With no stops at all (every control disabled while an
 *  operation runs) focus stays where it is rather than walking out to the
 *  page behind the backdrop. */
export function wrapTabTarget<T>(
  stops: readonly T[],
  active: T,
  backward: boolean,
): T | null {
  if (stops.length === 0) return active;
  const index = stops.indexOf(active);
  const first = stops[0] as T;
  const last = stops[stops.length - 1] as T;
  if (index === -1) return backward ? last : first;
  if (!backward && index === stops.length - 1) return first;
  if (backward && index === 0) return last;
  return null;
}

/** Where a Tab press lands when focus sits in a menu the modal opened
 *  into a portal (a select's list, portalled to the body so it clears
 *  the backdrop): the stop after the menu's trigger, or before it on
 *  Shift+Tab, wrapping at the ends — as if Tab had been pressed on the
 *  trigger itself. With no open trigger in the modal, the trap's usual
 *  entry from outside the stops. */
export function portalTabTarget<T>(
  stops: readonly T[],
  trigger: T | null,
  backward: boolean,
): T | null {
  if (stops.length === 0) return null;
  const index = trigger === null ? -1 : stops.indexOf(trigger);
  if (index === -1)
    return backward ? (stops[stops.length - 1] as T) : (stops[0] as T);
  const next = (index + (backward ? -1 : 1) + stops.length) % stops.length;
  return stops[next] as T;
}

const FOCUSABLE =
  'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]';

/** Where an element sits in the tab sequence, as the browser sees it. A
 *  contenteditable region (a CodeMirror editor's content) is in the
 *  sequence, but its `tabIndex` property still reads -1 when no tabindex
 *  attribute is set, so it takes the sequence's default of 0 there; an
 *  explicit attribute is honoured either way. */
export function sequentialTabIndex(el: {
  tabIndex: number;
  hasAttribute(name: string): boolean;
  getAttribute(name: string): string | null;
}): number {
  if (el.hasAttribute("tabindex")) return el.tabIndex;
  return el.getAttribute("contenteditable") === "true" ? 0 : el.tabIndex;
}

/** The container's tab stops in document order: enabled, in the tab
 *  sequence, and laid out (a hidden control is skipped like the browser
 *  skips it). */
export function tabStopsWithin(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) =>
      sequentialTabIndex(el) >= 0 &&
      !(el as HTMLButtonElement).disabled &&
      el.getAttribute("aria-hidden") !== "true" &&
      el.getClientRects().length > 0,
  );
}

/** A keydown handler for the modal's root: on Tab, moves focus where the
 *  trap says and swallows the key; every other key passes through. A Tab
 *  from a menu the modal portalled to the body (its keydown still bubbles
 *  here through React) continues from the menu's trigger, the control in
 *  the card marked expanded. */
export function trapTabKey(event: {
  key: string;
  shiftKey: boolean;
  currentTarget: EventTarget | null;
  preventDefault: () => void;
}) {
  if (event.key !== "Tab") return;
  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;
  const active = document.activeElement as HTMLElement;
  const stops = tabStopsWithin(root);
  const target =
    !root.contains(active) && active?.closest("[data-floating-ui-portal]")
      ? portalTabTarget(
          stops,
          root.querySelector<HTMLElement>('[aria-expanded="true"]'),
          event.shiftKey,
        )
      : wrapTabTarget(stops, active, event.shiftKey);
  if (target === null) return;
  event.preventDefault();
  target.focus();
}
